import { Component } from "react";
import { AlertTriangle, RotateCcw, ArrowLeft, Copy } from "lucide-react";

// Per-route safety net so a single component render error doesn't blank
// the whole app. The fallback offers Reload + Go back; users see what
// failed instead of the React white-screen-of-death.
//
// Wrap each major route in router/index.jsx via `errorElement` OR mount
// this around the Outlet inside each layout. Both are valid; we use the
// layout approach so layouts still render around the error.
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    this.setState({ info });
  }
  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || "Something went wrong.";
    const stack = this.state.error?.stack || "";
    const componentStack = this.state.info?.componentStack || "";
    // Top 6 source frames — production sourcemaps resolve minified
    // identifiers back to the original file, so operators can send
    // one screenshot and get a targeted fix.
    const topFrames = stack
      .split("\n")
      .slice(0, 7)
      .join("\n");
    const diagnosticBundle = `${msg}\n\n--- stack ---\n${topFrames}\n\n--- component ---\n${componentStack.trim()}`;
    const onCopy = () => {
      try {
        navigator.clipboard.writeText(diagnosticBundle).then(() =>
          this.setState({ copied: true }),
        );
      } catch { /* clipboard blocked — user still sees the text */ }
    };
    return (
      <div className="min-h-[40vh] flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-bg-primary border border-rose-500/40 rounded-xl p-5 shadow-xl">
          <div className="flex items-center gap-2 text-rose-300 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <h2 className="text-base font-semibold">Something broke on this page</h2>
          </div>
          <p className="text-sm text-text-secondary mb-3 break-words">
            {msg}
          </p>
          <p className="text-[11px] text-text-secondary mb-3">
            The rest of the app is unaffected. Reload to try again, or use
            your browser back button to go elsewhere.
          </p>
          {(stack || componentStack) && (
            <details className="mb-4 rounded border border-bg-secondary bg-bg-secondary/50">
              <summary className="cursor-pointer px-3 py-2 text-[11px] text-text-secondary hover:text-text-primary select-none">
                Show diagnostic details
              </summary>
              <pre className="px-3 pb-3 text-[10px] leading-relaxed text-text-secondary whitespace-pre-wrap break-all font-mono max-h-64 overflow-auto">
                {diagnosticBundle}
              </pre>
              <div className="px-3 pb-3">
                <button
                  onClick={onCopy}
                  className="px-2 py-1 rounded bg-bg-primary border border-bg-secondary text-[11px] text-text-secondary hover:text-text-primary inline-flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" />
                  {this.state.copied ? "Copied" : "Copy details"}
                </button>
              </div>
            </details>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 rounded bg-button-primary text-white text-sm inline-flex items-center gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reload
            </button>
            <button
              onClick={() => window.history.back()}
              className="px-3 py-1.5 rounded bg-bg-secondary text-text-secondary text-sm inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          </div>
        </div>
      </div>
    );
  }
}
